import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { calcPercentageRange } from '@xto10x/core/lib/app.utils';
import { styled } from '@peoplecues/commons';
import { dim } from 'styles/theme';

const StyledTextarea = styled.div`
  display: flex;
  flex-direction: column;

  .textAreaStyle {
    border-radius: 4px;
    resize: ${props => (props.allowResize ? 'vertical' : 'none')};
    height: ${props => (props.rows ? 'auto' : '164px')};
    width: ${props => props.width || 'auto'};
    padding: ${props =>
      props.narrowPadding ? `${dim._8px} ${dim._16px}` : `${dim._16px}`};
    color: ${props => props.theme.color.base_20};
    background-color: ${props => {
      if (props.disabled) return props.theme.color.button_disabled;
      if (props.error) return props.theme.color.red_100;
      if (props.type === 'focus') return props.theme.color.white;
      return props.theme.color.base_100;
    }};
    transition: border-color 0.2s ease-in;
    box-shadow: ${props => {
      if (props.disableBorder) return '';
      return props.showError
        ? '0 1px 2px 0 rgba(223, 46, 46, 0.25), 0 0 1px 0 rgba(223, 46, 46, 0)'
        : '0 1px 2px 0 rgba(9, 30, 66, 0.15), 0 0 1px 0 rgba(9, 30, 66, 0)';
    }};
    border: 1px solid
      ${props => {
        if (props.disableBorder) return '#fff';
        return props.error
          ? props.theme.color.tomato
          : props.theme.color.base_70;
      }};
    overflow: scroll;
    ::-webkit-scrollbar {
      display: none;
    }

    :hover {
      border-color: ${props => {
        if (props.disableBorder) return '';
        return props.error ? props.theme.color.tomato : '#adaeb5';
      }};
      box-shadow: ${props =>
        props.disableBorder
          ? ''
          : '0 1px 3px 0 rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0)'};
    }

    :focus {
      border-color: ${props => {
        if (props.disableBorder) return '';
        return props.error
          ? props.theme.color.tomato
          : props.theme.color.blue_50;
      }};
      box-shadow: ${props =>
        props.disableBorder
          ? ''
          : '0 1px 3px 0 rgba(47, 83, 215, 0.25), 0 0 1px 0 rgba(47, 83, 215, 0)'};
    }

    .errorMsg {
      display: flex;
      color: ${props => props.theme.color.tomato};
      padding-top: ${dim._10px};
    }
  }
`;

const TextareaRaw = React.memo(function TextareaRaw(props) {
  const [type, setType] = useState('default');

  const containerRef = useRef(null);

  const _handleStateChange = val => {
    if (type === 'focus' && val === 'default') {
      return;
    }
    if (type === 'focus' && val === 'enter') {
      return;
    }
    setType(val);
    if (props.checkIfFocus) {
      props.checkIfFocus();
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;
    if (props.autoFocus) {
      setType('focus');
      const elem = containerRef.current;
      const end = elem?.value?.length;
      if (elem.setSelectionRange) {
        elem.focus();
        elem.setSelectionRange(end, end);
      } else elem.focus();
    }
  }, [containerRef, props.autoFocus]);

  return (
    <StyledTextarea
      {...props}
      ref={containerRef}
      hidden={props.hidden}
      type={type}>
      <textarea
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={type === 'focus'}
        className={`textAreaStyle ${
          props.className ? props.className : 'body16'
        }`}
        placeholder={props.placeholder || 'Text area'}
        onChange={props.onValueChange}
        minLength={props.minLength}
        maxLength={props.maxLength}
        onMouseEnter={() => _handleStateChange('enter')}
        onMouseLeave={() => _handleStateChange('default')}
        onBlur={() => {
          _handleStateChange('blur');
          if (props.checkBlurForError) {
            props.checkBlurForError();
          }
          if (props.onBlur) props.onBlur();
        }}
        onFocus={() => {
          _handleStateChange('focus');
          if (props.onFocus) props.onFocus();
        }}
        rows={props.rows}
        value={props.value}
        tabIndex={props.tabIndex ? '-1' : '0'}
      />
      {props.children}
      {props.showWordLimit && (
        <div
          className={`wordLimit tag14 ${
            props.value
              ? calcPercentageRange(props.value.length, props.maxLength)
              : ''
          }`}>
          {props.value && props.value.length
            ? props.maxLength - props.value.length
            : props.maxLength}
        </div>
      )}
      {props.error && <div className="errorMsg">{props.errorMsg}</div>}
    </StyledTextarea>
  );
});

export default TextareaRaw;

const defaultFunction = () => {};

TextareaRaw.propTypes = {
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onValueChange: PropTypes.func,
  rows: PropTypes.number,
  placeholder: PropTypes.string
};

TextareaRaw.defaultProps = {
  onValueChange: defaultFunction(),
  error: false,
  errorMsg: 'Error Message',
  rows: 0,
  placeholder: 'PlaceHolder'
};
